import React, { useContext, useState, useEffect, useRef } from "react";
import { io, Socket } from 'socket.io-client';
import styled from 'styled-components';
import ChatGlobalContext from "../context/ChatGlobalContext";
import Message from "./Message";
import moment from 'moment';
import { readPremiseToken } from '../../../utils/premiseToken';
import { categories, defaultMessages, initialTwilioChatState, newAccountHolder, notAccountHolder, userTypeLabels, yesAccountHolder } from "../utils/chats";
import { chatLocalStorage, connectToWebsocket, createConversation, didAgentPost, fetchAsync, getAllMessages, getFiles, getToken, scrollToBottom, sendMessage } from "../services/chatService";
import { v4 as uuidv4 } from 'uuid';
import TypingIndicator from "./TypingIndicator";
import ChatContentList from "./styled/ChatContentList";
import Splitter from "./styled/Splitter";

interface Message {  
  createdAt: string;
  from?: string | null;
  files?: string | null;
  message: string | null;
  form: FormType | null;
  messageId: string;
  fromId: string;
  senderType: string;
}

interface JwtResponse {
  NetomniaSalesStatus: string | null;
  SalesStatus: string | null;
  BuildStatus: string | null;
  IsBDUK: string | null;
  Providers: string | null;
  iat: number | null;
  exp: number | null;
}

interface FormType {
  type: string;
  value: Array<{ title: string; action: string; placeholder?: string | null; type?: string; required?: boolean; options?:object }>;
}
  
type UserType = 'NEW_CUSTOMER' | 'ACCOUNT_HOLDER' | 'NOT_ACCOUNT_HOLDER';

const MessageList = ({...props}) => {
  const gContext: any = useContext(ChatGlobalContext);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [chatForm, setChatForm] = useState<{ [key: string]: any }>({});
  const [messages, setMessages] = useState<Message[]>(defaultMessages);
  const messagesRef = useRef<Message[]>(messages);
  const sc = useRef<Socket | null>(null);

  const [allowCreateChat, setAllowCreateChat] = useState(true);
  const messageRef = useRef<Message[]>([]);
  const [message, setMessage] = useState<Message|null>(null);
  const [newMessage, setNewMessage] = useState<boolean>(false);

  useEffect(() => {
    gContext?.goSetTwilioChat(chatLocalStorage());
  }, []);
  
  useEffect(() => {
    if(typeof document !== "undefined" && gContext?.goGetTwilioChat()?.chatUserId){
      const tmpUUID = chatSessionId || uuidv4();
      setChatSessionId(tmpUUID);
      localStorage.setItem('chatSessionId', tmpUUID);  
      if(!sc.current) sc.current = connectToWebsocket(tmpUUID);
      sc.current?.on('connect', () => {
        console.log('Connected to WebSocket server');
      });
      sc.current?.on("connect_error", (err) => {
        console.log(`Connect error due to ${err.message}`);
        sc.current = null;
        sc.current = connectToWebsocket(gContext?.goGetTwilioChat()?.chatUserId);
      });  
      sc.current?.on("disconnect", (reason) => {
        console.log(`Disconnected: ${reason}`);
        sc.current = null;
        sc.current = connectToWebsocket(gContext?.goGetTwilioChat()?.chatUserId);
      });
    } else {
      setMessages(defaultMessages);
    }
  }, [gContext?.goGetTwilioChat()?.chatUserId]);
  
  useEffect(() => {
    if(gContext?.goGetTwilioChat()?.chatCaseId){
      // Subscribe to room with Case ID
      sc.current && gContext?.goGetTwilioChat()?.chatCaseId && sc.current.emit("joinRoom", gContext?.goGetTwilioChat()?.chatCaseId);  
      // Listen events only for our Case/Conversation
      sc.current && !sc.current.listeners(`case-event-${gContext?.goGetTwilioChat()?.chatCaseId}`).length && sc.current?.on(`case-event-${gContext?.goGetTwilioChat()?.chatCaseId}`, (data) => {    
        switch(data.type){
          case "CASE_UPDATED": 
            gContext.twilioChat.agentId = data.data.properties?.OwnerId;
            // gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), agentId: gContext?.goGetTwilioChat()?.agentId});
            break;
          case "CONVERSATION_CLOSED":
            gContext?.goSetTwilioChat({
              ...initialTwilioChatState,
              openChat: gContext?.goGetTwilioChat()?.openChat,
              fullscreen: gContext?.goGetTwilioChat()?.fullscreen,
              side: gContext?.goGetTwilioChat()?.side,
              chatClosing: data.data.properties.ClosedBy !== 'CUSTOMER'
            });
            gContext?.goGetTwilioChat()?.chatCaseId && sc?.current?.emit("leaveRoom", gContext?.goGetTwilioChat()?.chatCaseId);
            break;
          case "MESSAGE_CREATED": receiveMessage(data.data, true); break;
          default: break;
        }
      });
    }
  }, [gContext?.goGetTwilioChat()?.chatCaseId]);
  
  useEffect(() => {
    if(message){
      messagesRef.current = messagesRef.current?.filter(item => !item.form);
      setMessages(messagesRef.current);
      scrollToBottom();
      setTimeout(() => {
        // Merge new messages with existing ones with unique messageId
        const tmpMessages = [...new Map([message, ...messagesRef.current].map(item => [item.messageId, item])).values()];
        messagesRef.current = tmpMessages;
        let tmpState = {
          ...gContext?.goGetTwilioChat(), 
          messages: tmpMessages, 
          chatUnreaded: gContext?.goGetTwilioChat()?.chatConversationId && (!gContext?.goGetTwilioChat()?.openChat || !gContext?.goGetTwilioChat()?.visibilityChange),
          typingAgent: false, 
          agentId: gContext?.goGetTwilioChat()?.agentId, 
          lastTimeActive: Date.now()
        };
        // Check if Agent has posted to start timer
        if(didAgentPost(messages)){ 
          tmpState = {...tmpState, lastTimeActive: Date.now()};
        }          
        gContext?.goSetTwilioChat(tmpState);
        scrollToBottom();
        setMessage(null);
      }, 500);
    }
  }, [message]);
  
  useEffect(() => {
    setMessages(gContext?.goGetTwilioChat()?.messages);
    messagesRef.current = gContext?.goGetTwilioChat()?.messages;
  }, [gContext?.goGetTwilioChat()?.messages]);
  
  let newMessageInterval:any;
  
  useEffect(() => {
    // Buffer for new messages
    if(newMessage && messageRef.current.length > 0 && !newMessageInterval){
      newMessageInterval = setInterval(() => {
        const [first, rest] = [messageRef.current[0], messageRef.current.slice(1) || []];
        messageRef.current = rest;
        setMessage(first);
        scrollToBottom();
        if(messageRef.current.length === 0){
          setNewMessage(false);
          clearInterval(newMessageInterval);
        }
      }, 600);   
    } else if (!newMessage) {
      setNewMessage(false);
      clearInterval(newMessageInterval);
    } else {

    }
  }, [newMessage]);
    
  const buttonAction = (action) => {
    if(gContext?.goGetTwilioChat()?.chatActive || gContext?.goGetTwilioChat()?.chatCaseId){
      setTimeout(() => {
        switch(action){
          case 'not_support_chat': 
            gContext?.goSetTwilioChat({
              ...gContext?.goGetTwilioChat(),   
              question: true,
              questionBtn2Action: 'close_question',
              questionBtn2Title: 'Close',
              questionTitle: "We've moved chat to the YouFibre app",
              questionMessage: gContext.appMessages?.chat_support_text || "",
            });
            break;
          case 'new_customer': 
            gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), chatUserType: 'NEW_CUSTOMER'});
            messageRef.current = newAccountHolder;
            setNewMessage(true);
            break;
          case 'not_customer': 
            gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), chatUserType: 'NOT_ACCOUNT_HOLDER'});
            messageRef.current = notAccountHolder;
            setNewMessage(true);
            break;
          case 'old_customer': 
            gContext?.goSetTwilioChat({...gContext?.goGetTwilioChat(), chatUserType: 'ACCOUNT_HOLDER'});
            messageRef.current = yesAccountHolder;
            setNewMessage(true);
            break;
          case 'submit_form':
              chatForm.firstName && chatForm.lastName && 
              chatForm.address && chatForm.address !== '/' && 
              chatForm.email && 
              gContext?.goGetTwilioChat()?.chatUserType === "NEW_CUSTOMER" && 
              submitForm();
            break;
          default: break;
        }
      }, 500);
    }
  };

  const submitForm = async () => { 
    try {
      let tmpState = {
        ...gContext?.goGetTwilioChat(), 
        chatLoading: true, 
        allowCreateChat: false,
        firstName: chatForm.firstName,
        lastName: chatForm.lastName,
        email: chatForm.email,
        phone: chatForm.phone,
        address: chatForm.address,
        postalCode: chatForm.postalCode,
        SPRN: chatForm.sprn,
        UDPRN: chatForm.udprn,
        UPRN: chatForm.uprn,
        UMPRN: chatForm.umprn,
        visibilityChange: true, 
      };
      gContext?.goSetTwilioChat(tmpState);
      const response = await fetchAsync(`/CrmModule/v2.0/premises/${chatForm.sprn}`, 'get', null, null);
      // Check if the response is okay
      if (response?.data?._sessionId) {
        const token = response.data._sessionId;
        let decoded: JwtResponse;
        try {
          decoded = readPremiseToken(token) as JwtResponse;
          tmpState = {...tmpState, 
            chatLoading: false, 
            NetomniaSalesStatus: decoded.NetomniaSalesStatus, 
            SalesStatus: decoded.SalesStatus,
            BuildStatus: decoded.BuildStatus,
            IsBDUK: decoded.IsBDUK,
            Providers: decoded.Providers,
            iat: decoded.iat,
            exp: decoded.exp
          };
        } catch (err) {
          tmpState = {...tmpState, 
            chatLoading: false,
            NetomniaSalesStatus: null,
            SalesStatus: null,
            BuildStatus: null,
            IsBDUK: 'false',
            Providers: null,
            iat: null,
            exp: null
          };
        }
        if(decoded.SalesStatus && decoded.SalesStatus?.toUpperCase() === "ORDER"){
          tmpState = {
            ...tmpState, 
            question: true,
            questionBtn1Action: 'no_consent',
            questionBtn1Title: 'No',
            questionBtn2Action: 'yes_consent',
            questionBtn2Title: 'Yes',
            questionTitle: "Before we chat",
            questionMessage: "Are you happy for us to get in touch by email or text if this chat gets disconnected?",
          };
        } else {
          tmpState = {
            ...tmpState, 
            question: true,
            questionBtn1Action: 'no_consent',
            questionBtn1Title: 'No',
            questionBtn2Action: 'yes_consent',
            questionBtn2Title: 'Yes',
            questionTitle: "Thanks for registering",
            questionMessage: "When your address is YouFibre-ready, would you like us to email, call or text you to let you know?",
          };
        }
        gContext?.goSetTwilioChat(tmpState);
      } 
    } catch (error) { console.error('Error fetching conversation status: ', error); }
  };
  
  const receiveMessage = async (data:any, resetTimer:boolean) => {
    if(data && gContext?.goGetTwilioChat()?.messages && !gContext?.goGetTwilioChat()?.messages.some(obj => obj.messageId === data.id)){
      try{
        if(data.properties.Files){
          let tmpFiles:any = await getFiles(data.properties.Files);
          if(Object.keys(tmpFiles).length === 0) tmpFiles = null;
          updateMessage(data, tmpFiles, resetTimer);
        } else {
          updateMessage(data, [], resetTimer);
        }
      } catch (error) {
        console.error('Error fetching message:', error);
      }
    }
  };

  const updateMessage = (data: any, files:any, resetTimer:boolean) => {
    let tmpMessage:Message = {
      senderType: data.type !== 'EVENT' ? data.properties.SenderType : data.type,
      createdAt: moment(new Date()).format(),
      from: data.type !== 'EVENT' ? data.properties.From : data.type,
      message: data.type !== 'EVENT' ? data.properties.Body : data.title,
      form: null,
      files: files,
      messageId: data.id,
      fromId: data.type !== 'EVENT' ? data.createdBy.id : "YouFibre"
    };
    scrollToBottom();
    messageRef.current = [ ...messageRef.current, tmpMessage];
    setNewMessage(true);
  };

  return (
    <ChatArea>
      <ChatContentList id='ChatContentList'>
        { sc.current && <TypingIndicator sc={sc} /> }
        { message && <><Animation>   
          <Splitter />     
            {/* { (Object.keys(message).length - 1) !== i  && <Splitter /> }   */}
            <Message item={message} buttonAction={buttonAction} setChatForm={setChatForm} />
          </Animation></>
        }
        { messages && messages.map((item, index) => 
          <>
          { item.senderType && <Message item={item} buttonAction={buttonAction} setChatForm={setChatForm} /> }
          { (Object.keys(messages).length - 1) !== index  && <Splitter /> }
          </>
        )}
      </ChatContentList>
    </ChatArea>
  );
};

const ChatArea = styled.div`
  width: 100%;
  flex: 1;
  overflow-x: hidden;
  display: flex;
  flex-direction: column-reverse;
`;

const Animation = styled.div`
  &, .animation {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: inherit;
    margin-bottom: -64px;
    animation: slideIn .4s cubic-bezier(0.41, 0.39, 0.53, 1.38) forwards;
  }
  @keyframes slideIn {
    from {
      margin-bottom: -64px;
    }
    to {
      margin-bottom: 0px;
    }
  }
`;

export default MessageList;
